import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { Button, IconButton } from '../ui';
import { CloseIcon } from '../icons/CloseIcon';
import { LockIcon } from '../icons/LockIcon';
import { CircleCheckIcon } from '../icons/CircleCheckIcon';
import { CheckIcon } from '../icons/CheckIcon';
import { BackArrowIcon } from '../icons/BackArrowIcon';
import { SearchIcon } from '../icons/SearchIcon';
import { UserIcon } from '../icons/UserIcon';
import { EyeIcon } from '../icons/EyeIcon';

type EntriView = 'intro' | 'analyzing' | 'provider' | 'login' | 'authenticating' | 'success';

const FLOW_STEPS: Partial<Record<EntriView, number>> = {
  analyzing: 1,
  provider: 2,
  login: 3,
  authenticating: 4,
};

type EntriModalProps = {
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
};

export function EntriModal({ open, onClose, onContinue }: EntriModalProps) {
  const [view, setView] = useState<EntriView>('intro');
  const [provider, setProvider] = useState('iwantmyname');
  const [search, setSearch] = useState('iwantmy');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (open) {
      setView('intro');
      setProvider('iwantmyname');
      setSearch('iwantmy');
      setEmail('');
      setPassword('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (view === 'analyzing') {
      const t = setTimeout(() => setView('provider'), 2500);
      return () => clearTimeout(t);
    }
    if (view === 'authenticating') {
      const t = setTimeout(() => setView('success'), 2200);
      return () => clearTimeout(t);
    }
  }, [open, view]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleBack = () => {
    if (view === 'login') setView('provider');
    else if (view === 'provider') setView('intro');
  };

  const showBack = view === 'provider' || view === 'login';
  const flowStep = FLOW_STEPS[view];
  const showClose = view !== 'success';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-[rgba(29,23,22,0.59)]"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        className="relative flex max-h-[90vh] w-[418px] flex-col overflow-y-auto rounded-[10px] bg-white p-l shadow-[0_23px_43px_rgba(0,0,0,0.19),0_6px_7px_rgba(0,0,0,0.09),0_2px_1.5px_rgba(0,0,0,0.05),0_1px_0.5px_rgba(0,0,0,0.05)]"
      >
        {(showBack || flowStep || showClose) && (
          <ModalHeader
            showBack={showBack}
            onBack={handleBack}
            flowStep={flowStep}
            showClose={showClose}
            onClose={onClose}
          />
        )}

        {view === 'intro' && <IntroBody onContinue={() => setView('analyzing')} />}
        {view === 'analyzing' && <AnalyzingBody />}
        {view === 'provider' && (
          <ProviderBody
            search={search}
            onSearchChange={setSearch}
            onSelect={name => {
              setProvider(name);
              setView('login');
            }}
          />
        )}
        {view === 'login' && (
          <LoginBody
            provider={provider}
            email={email}
            password={password}
            onEmailChange={setEmail}
            onPasswordChange={setPassword}
            onContinue={() => setView('authenticating')}
          />
        )}
        {view === 'authenticating' && (
          <AuthenticatingBody
            provider={provider}
            email={email || 'wangkendrick@gmail.com'}
            password={password || 'password123456'}
          />
        )}
        {view === 'success' && <SuccessBody onDone={onContinue} />}
      </div>
    </div>
  );
}

function ModalHeader({
  showBack,
  onBack,
  flowStep,
  showClose,
  onClose,
}: {
  showBack: boolean;
  onBack: () => void;
  flowStep: number | undefined;
  showClose: boolean;
  onClose: () => void;
}) {
  return (
    <div className="-mx-l -mt-l mb-2 flex h-9 items-center justify-between px-3">
      <div className="flex w-9 items-center">
        {showBack && (
          <IconButton aria-label="Back" onClick={onBack}>
            <BackArrowIcon />
          </IconButton>
        )}
      </div>
      <div className="flex flex-1 items-center justify-center">
        {flowStep && <StepDots current={flowStep} />}
      </div>
      <div className="flex w-9 items-center justify-end">
        {showClose && (
          <IconButton aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        )}
      </div>
    </div>
  );
}

function StepDots({ current, total = 5 }: { current: number; total?: number }) {
  return (
    <div className="flex items-center">
      {Array.from({ length: total }).map((_, i) => {
        const filled = i + 1 <= current;
        return (
          <Fragment key={i}>
            <div
              className={`size-[6px] rounded-full ${filled ? 'bg-content' : 'bg-[rgba(57,48,45,0.20)]'}`}
            />
            {i < total - 1 && <div className="h-px w-[18px] bg-[rgba(57,48,45,0.20)]" />}
          </Fragment>
        );
      })}
    </div>
  );
}

function IntroBody({ onContinue }: { onContinue: () => void }) {
  return (
    <div className="flex flex-col gap-m">
      <BrandHandoff />

      <div className="flex flex-col items-center gap-1 text-center">
        <h2 className="text-[16px] font-medium leading-[22px] text-content">
          Flodesk uses Entri to set up your domain
        </h2>
        <p className="text-[12px] font-normal leading-[17.8px] text-content-muted">
          You&apos;re a few steps away from completing authentication
        </p>
      </div>

      <div className="flex flex-col gap-m">
        <FeatureRow
          icon={<LockIcon />}
          title="Secure"
          description="Encrypted login protects your personal data"
        />
        <FeatureRow
          icon={<CircleCheckIcon />}
          title="Easy"
          description="No developer needed, automatically configure your domain settings to work"
        />
      </div>

      <Button className="w-full" onClick={onContinue}>
        Continue
      </Button>

      <p className="text-center text-[10px] font-normal leading-[14px] text-content-muted">
        By selecting &apos;Continue&apos; you agree to the Entri{' '}
        <a className="underline" href="#" onClick={e => e.preventDefault()}>
          Terms of Service
        </a>{' '}
        and{' '}
        <a className="underline" href="#" onClick={e => e.preventDefault()}>
          Privacy Policy
        </a>
      </p>
    </div>
  );
}

function AnalyzingBody() {
  return (
    <div className="flex flex-col items-center gap-m pt-2">
      <BrowserIllustration decorative />
      <h2 className="text-[16px] font-medium leading-[22px] text-content">Analyzing your domain</h2>
      <ul className="flex w-full flex-col items-center gap-3">
        <StatusItem>Analyzed kendrickwang.com</StatusItem>
        <StatusItem>Select DNS Provider Manually</StatusItem>
        <StatusItem active>Getting your setup details.</StatusItem>
      </ul>
      <PoweredByEntri />
    </div>
  );
}

function ProviderBody({
  search,
  onSearchChange,
  onSelect,
}: {
  search: string;
  onSearchChange: (v: string) => void;
  onSelect: (name: string) => void;
}) {
  const providers: Provider[] = [
    { id: 'iwantmyname', name: 'iwantmyname', domain: 'iwantmyname.com' },
    { id: 'godaddy', name: 'GoDaddy', domain: 'godaddy.com' },
    { id: 'namecheap', name: 'Namecheap', domain: 'namecheap.com' },
    { id: 'squarespace', name: 'Squarespace', domain: 'squarespace.com' },
  ];
  const q = search.toLowerCase().trim();
  const results = q
    ? providers.filter(p => p.name.toLowerCase().includes(q) || p.domain.toLowerCase().includes(q))
    : providers;

  return (
    <div className="flex flex-col gap-m">
      <h2 className="text-center text-[16px] font-medium leading-[22px] text-content">
        Select your domain provider
      </h2>

      <label className="flex h-[40px] items-center gap-2 rounded-[6px] border border-[rgba(60,50,47,0.12)] px-3">
        <SearchIcon className="text-content-muted" />
        <input
          type="text"
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          className="flex-1 bg-transparent text-[14px] leading-[20px] text-content placeholder:text-content-muted focus:outline-none"
          placeholder="Search"
        />
      </label>

      <div className="flex flex-col">
        {results.map(p => (
          <button
            key={p.id}
            type="button"
            onClick={() => onSelect(p.name)}
            className="flex items-center justify-between rounded-[6px] py-2 transition-colors hover:bg-[#F8F7F7]"
          >
            <span className="flex items-center gap-3">
              <ProviderLogo name={p.name} />
              <span className="text-[14px] font-medium leading-[20px] text-content">{p.name}</span>
            </span>
            <span className="text-[12px] leading-[17.8px] text-content-muted">{p.domain}</span>
          </button>
        ))}
        {results.length === 0 && (
          <p className="py-3 text-center text-[12px] text-content-muted">No providers found.</p>
        )}
      </div>

      <p className="text-[10px] italic leading-[14px] text-content-muted">
        Any use of registered trademarks from DNS Providers is only used as necessary to clearly
        identify the relevant services and is not intended to imply or suggest any affiliation,
        sponsorship, endorsement, or business relationship.
      </p>
    </div>
  );
}

function LoginBody({
  provider,
  email,
  password,
  onEmailChange,
  onPasswordChange,
  onContinue,
}: {
  provider: string;
  email: string;
  password: string;
  onEmailChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onContinue: () => void;
}) {
  const [showPassword, setShowPassword] = useState(false);
  return (
    <div className="flex flex-col gap-m">
      <ProviderHeader provider={provider} />

      <p className="text-center text-[12px] leading-[17.8px] text-content">
        By logging in with your {provider} details, you give us{' '}
        <strong className="font-medium">one-time</strong> permission to connect your domain.
      </p>

      <div className="flex flex-col gap-2">
        <IconField icon={<UserIcon className="text-content-muted" />}>
          <input
            type="email"
            value={email}
            onChange={e => onEmailChange(e.target.value)}
            placeholder="Email address"
            className="flex-1 bg-transparent text-[14px] leading-[20px] text-content placeholder:text-content-muted focus:outline-none"
          />
        </IconField>
        <IconField
          icon={<LockIcon className="text-content-muted" />}
          trailing={
            <button
              type="button"
              onClick={() => setShowPassword(v => !v)}
              className="text-content-muted transition-colors hover:text-content"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <EyeIcon />
            </button>
          }
        >
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={e => onPasswordChange(e.target.value)}
            placeholder="Password"
            className="flex-1 bg-transparent text-[14px] leading-[20px] text-content placeholder:text-content-muted focus:outline-none"
          />
        </IconField>
        <a
          className="self-end text-[12px] leading-[17.8px] text-content underline"
          href="#"
          onClick={e => e.preventDefault()}
        >
          Forgot password?
        </a>
      </div>

      <Button className="w-full" onClick={onContinue}>
        Continue
      </Button>

      <p className="text-center text-[11px] leading-[16px] text-content-muted">
        If you signed up with a social account or utilize Passkey MFA, you&apos;ll need to{' '}
        <a
          className="font-medium text-content underline"
          href="#"
          onClick={e => e.preventDefault()}
        >
          go to our manual setup
        </a>
        .
      </p>

      <a
        className="self-center text-[12px] leading-[17.8px] text-content underline"
        href="#"
        onClick={e => e.preventDefault()}
      >
        » Or forward login to someone else
      </a>
    </div>
  );
}

function AuthenticatingBody({
  provider,
  email,
  password,
}: {
  provider: string;
  email: string;
  password: string;
}) {
  return (
    <div className="flex flex-col gap-m">
      <ProviderHeader provider={provider} />

      <p className="text-center text-[12px] leading-[17.8px] text-content">
        By logging in with your {provider} details, you give us{' '}
        <strong className="font-medium">one-time</strong> permission to connect your domain.
      </p>

      <div className="flex flex-col gap-2">
        <IconField icon={<UserIcon className="text-content-muted" />}>
          <input
            type="email"
            value={email}
            readOnly
            className="flex-1 bg-transparent text-[14px] leading-[20px] text-content focus:outline-none"
          />
        </IconField>
        <IconField icon={<LockIcon className="text-content-muted" />}>
          <input
            type="password"
            value={password}
            readOnly
            className="flex-1 bg-transparent text-[14px] leading-[20px] text-content focus:outline-none"
          />
        </IconField>
      </div>

      <LoadingButton>Authenticating…</LoadingButton>

      <p className="text-center text-[12px] leading-[17.8px] text-content-muted">
        This may take a few minutes.
      </p>

      <p className="text-center text-[11px] leading-[16px] text-content-muted">
        If you signed up with a social account or utilize Passkey MFA, you&apos;ll need to{' '}
        <a
          className="font-medium text-content underline"
          href="#"
          onClick={e => e.preventDefault()}
        >
          go to our manual setup
        </a>
        .
      </p>
    </div>
  );
}

function SuccessBody({ onDone }: { onDone: () => void }) {
  return (
    <div className="flex flex-col items-center gap-m">
      <BrowserIllustration showCheck />

      <div className="w-full rounded-[10px] border-2 border-[#E879A8] bg-[#FBE3EE] px-3 py-3">
        <p className="text-center text-[14px] font-normal leading-[20px] text-content">
          Records Added, head back to google postmaster and click verify
        </p>
      </div>

      <Button className="w-full" onClick={onDone}>
        Done
      </Button>

      <PoweredByEntri />
    </div>
  );
}

type Provider = { id: string; name: string; domain: string };

function ProviderLogo({ name }: { name: string }) {
  const initials = name.slice(0, 4).toLowerCase();
  return (
    <span className="flex size-7 items-center justify-center rounded bg-[#F3F2F1] text-[8px] font-medium tracking-tight text-content">
      {initials}
    </span>
  );
}

function ProviderHeader({ provider }: { provider: string }) {
  return (
    <div className="flex items-center justify-center gap-1 pt-1">
      <span className="size-2 rounded-full bg-[#E07856]" />
      <span className="text-[20px] font-medium leading-[24px] tracking-tight text-content">
        {provider}
      </span>
    </div>
  );
}

function IconField({
  icon,
  trailing,
  children,
}: {
  icon: ReactNode;
  trailing?: ReactNode;
  children: ReactNode;
}) {
  return (
    <label className="flex h-[40px] items-center gap-2 rounded-[6px] border border-[rgba(60,50,47,0.12)] px-3">
      <span className="shrink-0">{icon}</span>
      {children}
      {trailing && <span className="shrink-0">{trailing}</span>}
    </label>
  );
}

function LoadingButton({ children }: { children: ReactNode }) {
  return (
    <div className="relative h-[40px] w-full overflow-hidden rounded-[6px] bg-[#E7E5E4]">
      <div className="absolute inset-y-0 left-0 w-1/4 animate-[entri-pulse_1.6s_ease-in-out_infinite] bg-content" />
      <div className="relative flex h-full items-center justify-center text-[14px] font-medium leading-[20px] text-content-muted">
        {children}
      </div>
      <style>{`
        @keyframes entri-pulse {
          0% { transform: translateX(-25%); }
          100% { transform: translateX(400%); }
        }
      `}</style>
    </div>
  );
}

function StatusItem({ active, children }: { active?: boolean; children: ReactNode }) {
  return (
    <li className="flex items-center gap-3">
      <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-content text-white">
        <CheckIcon className="size-3" />
      </span>
      <span
        className={`text-[14px] leading-[20px] text-content ${active ? 'font-medium' : 'font-normal'}`}
      >
        {children}
      </span>
    </li>
  );
}

function PoweredByEntri() {
  return (
    <p className="flex items-center justify-center gap-1 text-[11px] leading-[16px] text-content-muted">
      <span className="flex size-3 items-center justify-center">
        <span className="size-[6px] rounded-full bg-content-muted" />
      </span>
      Powered by Entri
    </p>
  );
}

function BrandHandoff() {
  return (
    <div className="flex items-center justify-center gap-2 pt-1">
      <div className="flex size-[60px] items-center justify-center rounded-full bg-[#F3F2F1]">
        <span className="text-[11px] font-medium leading-none text-content">flodesk</span>
      </div>
      <div className="flex items-center text-content-muted" aria-hidden="true">
        <svg width="22" height="10" viewBox="0 0 22 10" fill="none">
          <path
            d="M1 7h7m6-4h7m-3 0 3 0-3 3m-9 4-3 0 3-3"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div className="flex size-[60px] items-center justify-center rounded-full bg-[#1A1A1A]">
        <div className="size-[10px] rounded-full bg-white" />
      </div>
    </div>
  );
}

function BrowserIllustration({
  decorative,
  showCheck,
}: {
  decorative?: boolean;
  showCheck?: boolean;
}) {
  return (
    <div className="relative flex h-[110px] w-full items-center justify-center">
      {decorative && (
        <>
          <span className="absolute left-6 top-6 size-2 rounded-full bg-content" />
          <span
            className="absolute left-2 top-12 size-0 border-x-[8px] border-b-[14px] border-x-transparent border-b-[#E0A24A]"
            style={{ borderLeftColor: 'transparent', borderRightColor: 'transparent' }}
          />
          <span className="absolute right-12 top-2 h-3 w-1 rotate-12 rounded-sm bg-[#5B6BFF]" />
          <span className="absolute right-4 bottom-3 size-2 rotate-45 bg-content" />
          <span className="absolute right-8 top-12 size-2 rounded-full bg-[#5B6BFF]" />
          <span className="absolute left-16 top-2 size-1.5 rounded-full bg-content-muted" />
        </>
      )}
      <div className="relative flex h-[88px] w-[160px] flex-col overflow-hidden rounded-[6px] border border-[#C9D1FF] bg-[#E2EAFF]">
        <div className="flex h-4 items-center gap-1 border-b border-[#C9D1FF] bg-[#D6DEFF] px-2">
          <span className="size-1.5 rounded-full bg-[#7C8AFF]" />
          <span className="size-1.5 rounded-full bg-[#7C8AFF]" />
          <span className="size-1.5 rounded-full bg-[#7C8AFF]" />
        </div>
        <div className="flex flex-1 flex-col gap-1.5 p-3">
          <div className="h-1.5 w-full rounded-full bg-[#7C8AFF]" />
          <div className="h-1.5 w-3/4 rounded-full bg-[#7C8AFF]" />
          <div className="h-1.5 w-5/6 rounded-full bg-[#7C8AFF]" />
        </div>
        {showCheck && (
          <span className="absolute -bottom-2 -left-2 flex size-9 items-center justify-center rounded-full border border-[rgba(60,50,47,0.12)] bg-white text-content">
            <CheckIcon className="size-4" />
          </span>
        )}
      </div>
      {showCheck && (
        <span className="absolute right-8 top-3 size-0 border-x-[10px] border-b-[16px] border-x-transparent border-b-[#E0A24A]" />
      )}
    </div>
  );
}

function FeatureRow({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-[2px] text-content">{icon}</span>
      <div className="flex flex-col gap-[2px]">
        <p className="text-[14px] font-medium leading-[20px] text-content">{title}</p>
        <p className="text-[12px] font-normal leading-[17.8px] text-content-muted">{description}</p>
      </div>
    </div>
  );
}
